import React, { useContext, useRef, useState } from 'react'
import {
  Dialog,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Box,
  IconButton,
  Slide,
  InputAdornment,
  useTheme,
  DialogContent
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ClearIcon from '@mui/icons-material/Clear'
import CheckIcon from '@mui/icons-material/Check'
import { TransitionProps } from '@mui/material/transitions'
import { Measure } from '../types'
import TabMeasureIcon from './TabMeasureIcon'
import { upperFirst } from '../utils/miscUtils'
import { AppContext } from '../contexts/AppProvider'
import { MeasureDataContext } from '../contexts/MeasureDataProvider'
import { highlightSearchTerm } from './helpers/highlightSearchTerm'
import { useAppNavigate } from '../hooks/useAppNavigate'


interface MeasureSearchMobileOverlayProps {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  onOpenNewMeasureRequestDialog: () => void
}

const Transition = React.forwardRef(function Transition (
    props: TransitionProps & {
    children: React.ReactElement<unknown>
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />
})

const MeasureSearchMobileOverlay: React.FC<MeasureSearchMobileOverlayProps> = ({
  open,
  setOpen,
  onOpenNewMeasureRequestDialog
}) => {
  const { appState } = useContext(AppContext)
  const { allMeasures } = useContext(MeasureDataContext)
  const { currentMeasure } = appState
  const { navigateToConverter } = useAppNavigate()
  const theme = useTheme()
  const [searchTerm, setSearchTerm] = useState('')
  const contentRef = useRef<HTMLDivElement>(null)
  const [startY, setStartY] = useState<number | null>(null)
  const [currentY, setCurrentY] = useState<number | null>(null)

  const filteredMeasures = Object.values(allMeasures).filter(option =>
    option.displayName.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleListItemClick = (newCurrentMeasure: Measure): void => {
    setOpen(false)
    setSearchTerm('')

    navigateToConverter(
      newCurrentMeasure,
      appState.tabs[newCurrentMeasure.codeName].fromUnit,
      appState.tabs[newCurrentMeasure.codeName].toUnit
    )
  }

  const handleClearOverlaySearch = (): void => {
    setSearchTerm('')
  }

  const onClose = (): void => {
    setOpen(false)
  }

  const handleTouchStart = (event: React.TouchEvent): void => {
    setStartY(event.touches[0].clientY)
  }

  const handleTouchMove = (event: React.TouchEvent): void => {
    setCurrentY(event.touches[0].clientY)
  }

  const handleTouchEnd = (): void => {
    if (startY !== null && currentY !== null) {
      const swipeDistance = currentY - startY
      const swipeThreshold = 150 // Adjust this threshold as needed

      const isAtTop =
        (contentRef.current && contentRef.current.scrollTop <= 0) || contentRef.current?.scrollTop === undefined

      // If the swipe distance is positive (downward swipe) and exceeds the threshold, close the dialog
      if ((swipeDistance > swipeThreshold) && isAtTop) {
        onClose()
      }
    }

    // Reset the swipe positions
    setStartY(null)
    setCurrentY(null)
  }

  const handleRequestMeasureClick = (): void => {
    setSearchTerm('')
    setOpen(false)
    onOpenNewMeasureRequestDialog()
  }


  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      scroll='paper'
      TransitionComponent={Transition}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      // Important: Adding these props to better manage focus
      keepMounted={false}
      // This prevents focusing back and forth during transitions (not sure what it means to be honest)
      disableRestoreFocus={true}
    >
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        padding="8px"
        sx={{
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)'
        }}>
        <IconButton
          onClick={onClose}
          sx={{ color: `${theme.palette.grey[500]}` }}
          aria-label="Close converter search overlay">
          <ArrowBackIcon />
        </IconButton>
        <TextField
          placeholder="Search converters..."
          variant="outlined"
          size="small"
          fullWidth
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          InputProps={{
            endAdornment: searchTerm && ( // Show "X" icon only if there's input
              <InputAdornment position="end">
                <IconButton
                  onClick={handleClearOverlaySearch}
                  edge="end"
                  size="small"
                  aria-label="Clear search">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
          sx={{
            '& .MuiOutlinedInput-input': {
              '&::placeholder': {
                color: `${theme.palette.grey[500]}`,
                opacity: 1
              }
            },
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                border: 'none'
              }
            }
          }}
        />
      </Box>

      {/* List of Options */}
      <DialogContent ref={contentRef} sx={{ padding: 0 }}>
        <List>
          {filteredMeasures.map(option => (
            <ListItem
              key={option.displayName}
              disablePadding
            >
              <ListItemButton
                onClick={() => handleListItemClick(option)}
                aria-label={`Switch to ${option.displayName} converter`}
              >
                <ListItemText primary={
                  <Box display="flex" alignItems="center">
                    <TabMeasureIcon
                      measure={option}
                      color={option.displayName === currentMeasure.displayName
                        ? theme.palette.custom.solidBeauty
                        : undefined}
                    />

                    <Box ml={1}
                      sx={option.displayName === currentMeasure.displayName
                        ? { color: theme.palette.custom.solidBeauty, fontWeight: 'bold' }
                        : {}}>

                      {highlightSearchTerm(upperFirst(option.displayName), searchTerm)}

                      </Box>
                  </Box>
                } />
                {/* Show the "V" icon for the selected option */}
                {option.displayName === currentMeasure.displayName && (
                  <ListItemIcon>
                    <CheckIcon color="primary" />
                  </ListItemIcon>
                )}
              </ListItemButton>
            </ListItem>
          ))}

          {searchTerm !== '' && filteredMeasures.length === 0 && (
            <ListItem disablePadding>
              <ListItemButton
                onClick={handleRequestMeasureClick}
                aria-label={`Request measure "${searchTerm}"`}>
                <ListItemText primary={highlightSearchTerm(`Request measure "${searchTerm}"`, searchTerm)} />
              </ListItemButton>
            </ListItem>
          )}
        </List>
      </DialogContent>
    </Dialog>
  )
}

export default MeasureSearchMobileOverlay
